import { useState, useCallback, useRef } from 'react'
import AgeGroupSelector from '../../components/AgeGroupSelector'
import LanguageSelector from '../../components/LanguageSelector'
import CrosswordGrid, { type CrosswordGridHandle } from './CrosswordGrid'
import { generateCrossword } from '../../lib/crossword/generator'
import { fetchWordBank } from '../../lib/api'
import type { WordEntry } from '../../lib/crossword/wordbank'
import {
  AgeGroup,
  CrosswordConfig,
  CrosswordPuzzle,
  CrosswordSize,
  CrosswordWord,
  Language,
  CROSSWORD_WORD_COUNTS,
  LANGUAGE_FLAGS,
} from '../../types'

// ─── Config ───────────────────────────────────────────────────────────────────

const defaultConfig: CrosswordConfig = {
  ageGroup: 'young',
  language: 'en',
  size:     'medium',
}

const SIZES: CrosswordSize[] = ['small', 'medium', 'large']
const SIZE_LABELS: Record<CrosswordSize, string> = {
  small:  'Small',
  medium: 'Medium',
  large:  'Large',
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CrosswordPage() {
  const gridRef = useRef<CrosswordGridHandle>(null)

  const [config, setConfig]   = useState<CrosswordConfig>(defaultConfig)
  const [puzzle, setPuzzle]   = useState<CrosswordPuzzle | null>(null)
  const [generating, setGenerating] = useState(false)
  const [checked, setChecked] = useState(false)
  const [solved, setSolved]   = useState(false)
  const [withSolution, setWithSolution] = useState(false)
  const [printing, setPrinting]         = useState(false)
  const [worksheetCount, setWorksheetCount] = useState(1)
  const [activeWord, setActiveWord] = useState<CrosswordWord | null>(null)

  // Cache the fetched word bank so PDF generation reuses the same pool
  const cachedWordsRef = useRef<WordEntry[] | null>(null)

  async function handleStart(cfg: CrosswordConfig) {
    setGenerating(true)
    setChecked(false)
    setSolved(false)
    setActiveWord(null)

    // Fetch from D1; fall back to static bank if unavailable
    const words = await fetchWordBank(cfg.language, cfg.ageGroup)
    cachedWordsRef.current = words  // may be null — generator handles that

    // Small timeout keeps the "Generating…" UI visible for a frame
    setTimeout(() => {
      setPuzzle(generateCrossword(cfg, words ?? undefined))
      setGenerating(false)
    }, 20)
  }

  function handleNew() {
    if (puzzle) handleStart(config)
  }

  const handleSelectionChange = useCallback((word: CrosswordWord | null) => {
    setActiveWord(word)
  }, [])

  async function handlePrint() {
    if (!puzzle) return
    setPrinting(true)
    try {
      const { createElement }       = await import('react')
      const { downloadPDF }         = await import('../../lib/pdf/download')
      const { CrosswordDocument }   = await import('../../lib/pdf/crossword')
      const { generateCrossword: gen } = await import('../../lib/crossword/generator')
      const bank = cachedWordsRef.current ?? undefined
      const puzzles = [
        puzzle,
        ...Array.from({ length: worksheetCount - 1 }, () => gen(config, bank)),
      ]
      const filename = worksheetCount > 1
        ? `crossword-worksheet-${worksheetCount}x-${config.language}.pdf`
        : `crossword-${config.language}-${config.size}.pdf`
      await downloadPDF(createElement(CrosswordDocument, { puzzles, withSolution }), filename)
    } finally {
      setPrinting(false)
    }
  }

  // ── Config screen ─────────────────────────────────────────────────────────

  if (!puzzle) {
    return (
      <div className="max-w-lg">
        <h1 className="font-display font-black text-3xl text-bubble-text mb-1">Crossword</h1>
        <p className="text-bubble-muted mb-8">Set up your crossword puzzle.</p>

        <div className="card p-6 space-y-6">
          <AgeGroupSelector
            value={config.ageGroup}
            onChange={(ageGroup: AgeGroup) => setConfig((p) => ({ ...p, ageGroup }))}
          />

          <LanguageSelector
            value={config.language}
            onChange={(language: Language) => setConfig((p) => ({ ...p, language }))}
          />

          <div>
            <label className="config-label">Size</label>
            <div className="flex gap-2" role="radiogroup" aria-label="Puzzle size">
              {SIZES.map((s) => (
                <button
                  key={s}
                  role="radio"
                  aria-checked={config.size === s}
                  onClick={() => setConfig((p) => ({ ...p, size: s }))}
                  className={config.size === s ? 'sel-btn-active' : 'sel-btn'}
                >
                  <span>{SIZE_LABELS[s]}</span>
                  <span className="text-xs font-normal text-bubble-muted mt-0.5">
                    ~{CROSSWORD_WORD_COUNTS[s].min}–{CROSSWORD_WORD_COUNTS[s].max} words
                  </span>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => handleStart(config)}
            disabled={generating}
            className="btn-primary w-full text-base py-3 disabled:opacity-50"
          >
            {generating ? 'Generating…' : 'Generate Puzzle →'}
          </button>
        </div>
      </div>
    )
  }

  // Separate across/down clues and sort by number
  const acrossClues = puzzle.words.filter((w) => w.direction === 'across').sort((a, b) => a.number - b.number)
  const downClues   = puzzle.words.filter((w) => w.direction === 'down').sort((a, b) => a.number - b.number)

  return (
    <div className="max-w-4xl">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="font-display font-black text-2xl text-bubble-text">
            Crossword {LANGUAGE_FLAGS[config.language]}
          </h1>
          <p className="text-sm text-bubble-muted capitalize">
            {config.size} · {puzzle.words.length} words · {puzzle.rows}×{puzzle.cols}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleNew}
            disabled={generating}
            className="btn-secondary text-sm px-4 py-2"
          >
            {generating ? '⏳' : '↺'} New
          </button>
          <label className="flex items-center gap-1 text-xs text-bubble-muted font-black cursor-pointer select-none">
            <input
              type="checkbox"
              checked={withSolution}
              onChange={(e) => setWithSolution(e.target.checked)}
              className="accent-bubble-coral"
            />
            +solution
          </label>
          <select
            value={worksheetCount}
            onChange={(e) => setWorksheetCount(Number(e.target.value))}
            className="text-xs font-black text-bubble-text border-2 border-gray-200 rounded-full px-3 py-1.5 bg-white cursor-pointer focus:outline-none focus:border-bubble-coral"
          >
            {[1, 2, 3, 5, 10].map((n) => (
              <option key={n} value={n}>{n === 1 ? '1 puzzle' : `${n} puzzles`}</option>
            ))}
          </select>
          <button
            onClick={handlePrint}
            disabled={printing}
            className="btn-primary text-sm px-4 py-2 disabled:opacity-60"
          >
            {printing ? '⏳ Generating…' : `🖨 ${worksheetCount > 1 ? 'Worksheet' : 'Print'}`}
          </button>
        </div>
      </div>

      {/* ── Solved banner ── */}
      {solved && (
        <div className="card border-bubble-teal bg-bubble-teal-light mb-4 p-4 text-center">
          <p className="font-display font-black text-xl text-teal-700 mb-1">🎉 Crossword complete!</p>
          <p className="text-sm text-teal-600">All words correct — well done!</p>
        </div>
      )}

      {/* ── Active clue banner ── */}
      {activeWord && !solved && (
        <div className="rounded-[16px] bg-bubble-lemon-light border-2 border-bubble-lemon px-4 py-2 mb-4 flex items-center gap-3">
          <span className="font-black text-yellow-800 text-sm whitespace-nowrap">
            {activeWord.number} {activeWord.direction === 'across' ? '→' : '↓'}
          </span>
          <span className="text-sm text-yellow-900">{activeWord.clue}</span>
        </div>
      )}

      {/* ── Grid + Clues ── */}
      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* Grid */}
        <div className="overflow-x-auto flex-shrink-0">
          <CrosswordGrid
            ref={gridRef}
            puzzle={puzzle}
            checked={checked}
            onComplete={() => setSolved(true)}
            onSelectionChange={handleSelectionChange}
          />
          {/* Special-character picker — shown for languages that need it */}
          {config.language === 'sl' && (
            <div className="flex gap-2 mt-2">
              {['Č', 'Š', 'Ž'].map((ch) => (
                <button
                  key={ch}
                  onMouseDown={(e) => { e.preventDefault(); gridRef.current?.typeChar(ch) }}
                  className="sel-btn text-base font-black px-4 py-2 min-w-[44px]"
                  aria-label={`Type ${ch}`}
                >
                  {ch}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Clue lists */}
        <div className="flex gap-6 flex-1 min-w-0 flex-wrap lg:flex-nowrap">
          <ClueList
            title="Across →"
            clues={acrossClues}
            activeWord={activeWord}
          />
          <ClueList
            title="Down ↓"
            clues={downClues}
            activeWord={activeWord}
          />
        </div>
      </div>

      {/* ── Check answers ── */}
      {!solved && (
        <div className="flex gap-3 mt-6 justify-center">
          <button
            onClick={() => setChecked(true)}
            className="btn-primary px-6 py-2.5 text-sm"
          >
            Check Answers
          </button>
          {checked && (
            <button
              onClick={() => setChecked(false)}
              className="btn-secondary px-6 py-2.5 text-sm"
            >
              Keep trying
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Clue list ────────────────────────────────────────────────────────────────

function ClueList({
  title,
  clues,
  activeWord,
}: {
  title:      string
  clues:      CrosswordWord[]
  activeWord: CrosswordWord | null
}) {
  return (
    <div className="min-w-[160px] flex-1">
      <h2 className="font-display font-black text-base text-bubble-text mb-2">{title}</h2>
      <ol className="space-y-1">
        {clues.map((w) => {
          const isActive = activeWord?.number === w.number && activeWord?.direction === w.direction
          return (
            <li
              key={`${w.direction}-${w.number}`}
              className={`text-sm rounded-[10px] px-2 py-1 transition-colors ${
                isActive
                  ? 'bg-bubble-lemon-light font-black text-yellow-900'
                  : 'text-bubble-text'
              }`}
            >
              <span className="font-black text-bubble-coral mr-1">{w.number}.</span>
              {w.clue}
            </li>
          )
        })}
      </ol>
    </div>
  )
}
